import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Modal } from "./bits";
import { useAuth } from "../context/AuthContext";
import { startTour } from "./Tour";

export const SampleBadge = () => {
  const [on, setOn] = useState(false);
  const refreshBadge = () => api.get("/demo/status").then((r) => setOn(!!r.data.loaded)).catch(() => {});
  useEffect(() => { refreshBadge(); window.addEventListener("xq-demo", refreshBadge); return () => window.removeEventListener("xq-demo", refreshBadge); }, []);
  useEffect(() => { document.body.classList.toggle("sample-mode", on); return () => document.body.classList.remove("sample-mode"); }, [on]);
  if (!on) return null;
  return <div className="sample-pill" data-testid="sample-data-pill" title="A demo account is active. Remove it from Accounts & sync."><Sparkles size={11} /> SAMPLE DATA</div>;
};

export const useDemo = (onChange) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => api.get("/demo/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.post("/demo/load"); toast.success(data.preview_until ? `Sample account loaded · ${data.trades} trades · Team metrics unlocked for 24h` : `Sample account loaded · ${data.trades || ""} trades over 6 months`); await refresh(); window.dispatchEvent(new Event("xq-demo")); if (onChange) onChange(data); else if (!data.already) startTour(); }
    catch (ex) { toast.error(errText(ex)); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete("/demo/remove"); toast("Sample account removed"); await refresh(); onChange?.(); window.dispatchEvent(new Event("xq-demo")); }
    catch (ex) { toast.error(errText(ex)); } finally { setBusy(false); }
  };
  return { st, busy, load, remove };
};

export const DemoBanner = ({ onChange }) => {
  const { st, busy, load, remove } = useDemo((data) => { onChange?.(); if (data && !data.already) { localStorage.setItem("xq_tour", "1"); window.location.reload(); } });
  if (!st) return null;
  if (st.loaded) return (
    <div className="note flex justify-between items-center gap-3 flex-wrap mb-3" data-testid="demo-loaded-banner">
      <span><b>Sample account active.</b> Charts, tables and metrics are showing demo trades tagged "demo".</span>
      <button className="btn danger sm" disabled={busy} onClick={remove} data-testid="demo-remove-btn">Remove sample data</button>
    </div>
  );
  return (
    <div className="note flex justify-between items-center gap-3 flex-wrap mb-3" data-testid="demo-offer-banner">
      <span><b>New here?</b> Load a sample account with 6 months of trades and journal notes to see every chart, table and metric alive.</span>
      <button className="btn sm" disabled={busy} onClick={load} data-testid="demo-load-btn"><Sparkles size={14} /> {busy ? "Loading…" : "Load sample account"}</button>
    </div>
  );
};

export const DemoWelcome = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { st, busy, load } = useDemo(() => { setOpen(false); localStorage.setItem("xq_tour", "1"); nav("/today"); window.location.reload(); });
  useEffect(() => {
    if (st && !st.has_accounts && !sessionStorage.getItem("demo_offer_seen") && !["admin", "superadmin"].includes(user?.role)) { setOpen(true); sessionStorage.setItem("demo_offer_seen", "1"); }
  }, [st, user]);
  return (
    <Modal open={open} onClose={() => setOpen(false)} title="Welcome to ExperTraQ" testId="demo-welcome-modal">
      <p style={{ color: "var(--ink-2)" }}>You have no accounts yet. Want a one-click sample account with 6 months of realistic trades so every screen feels alive from day one? You can remove it any time.</p>
      <div className="pill-row mt-3">
        <button className="btn" disabled={busy} onClick={load} data-testid="demo-welcome-load-btn"><Sparkles size={14} /> {busy ? "Loading…" : "Load sample account"}</button>
        <button className="btn ghost" onClick={() => { setOpen(false); nav("/accounts"); }} data-testid="demo-welcome-own-btn">Add my own account</button>
      </div>
    </Modal>
  );
};
